/**
 * PoliciesQuickStartModal
 * 
 * A 3-step wizard to configure policies in under 2 minutes.
 * Supports presets (DTC Default, Customer-First, Margin-First) and custom values.
 * 
 * Steps:
 * 1. Choose preset or default fulfillment stance
 * 2. Configure expedite threshold
 * 3. Configure refund threshold
 * 
 * Auto-creates all 5 policies with sensible defaults for:
 * - substitutions
 * - channel_buffers
 */

import React, { useState } from 'react';
import { quickstartPolicies } from '../api/policies';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CheckCircle, Package, Zap, DollarSign, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';

// Preset definitions
const PRESETS = {
  balanced: {
    label: 'DTC Default (Balanced)',
    description: 'Good balance between speed and margin protection',
    defaultFulfillment: 'split_shipment',
    maxExpedite: 50,
    expediteApprovalOver: 25,
    maxRefundNoApproval: 25,
    refundApprovalOver: 50,
    icon: Package
  },
  customer_first: {
    label: 'Customer-First',
    description: 'Prioritize customer experience, higher approval limits',
    defaultFulfillment: 'split_shipment',
    maxExpedite: 80,
    expediteApprovalOver: 50,
    maxRefundNoApproval: 40,
    refundApprovalOver: 80,
    icon: Zap
  },
  margin_first: {
    label: 'Margin-First',
    description: 'Strict approvals, ship complete by default',
    defaultFulfillment: 'ship_complete',
    maxExpedite: 35,
    expediteApprovalOver: 20,
    maxRefundNoApproval: 15,
    refundApprovalOver: 30,
    icon: DollarSign
  }
};

export default function PoliciesQuickStartModal({ open, onClose, brandId, onDone }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [preset, setPreset] = useState('balanced');
  const [defaultFulfillment, setDefaultFulfillment] = useState('split_shipment');
  const [maxExpedite, setMaxExpedite] = useState(50);
  const [expediteApprovalOver, setExpediteApprovalOver] = useState(25);
  const [maxRefundNoApproval, setMaxRefundNoApproval] = useState(25);
  const [refundApprovalOver, setRefundApprovalOver] = useState(50);

  // Apply preset values
  const applyPreset = (presetKey) => {
    const p = PRESETS[presetKey];
    setPreset(presetKey);
    setDefaultFulfillment(p.defaultFulfillment);
    setMaxExpedite(p.maxExpedite);
    setExpediteApprovalOver(p.expediteApprovalOver);
    setMaxRefundNoApproval(p.maxRefundNoApproval);
    setRefundApprovalOver(p.refundApprovalOver);
  };

  const handleClose = () => {
    setStep(1);
    onClose?.();
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      await quickstartPolicies({
        brand_id: brandId,
        preset: preset,
        default_fulfillment: defaultFulfillment,
        max_expedite_cost_usd: Number(maxExpedite),
        expedite_requires_approval_over_usd: Number(expediteApprovalOver),
        max_refund_without_approval_usd: Number(maxRefundNoApproval),
        refund_requires_approval_over_usd: Number(refundApprovalOver)
      });
      
      toast.success('Policy profile saved (v1.0)', {
        description: 'Decision Pack and Slack approvals are now policy-aware.'
      });
      
      onDone?.();
      handleClose();
    } catch (err) {
      console.error('Quick start failed:', err);
      toast.error('Failed to save policies', {
        description: err.message || 'Please try again'
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Policy Quick Start
          </DialogTitle>
          <DialogDescription>
            Configure minimum policy thresholds so Decision Pack and Slack approvals become policy-aware. Takes about 2 minutes.
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center gap-2 py-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`flex-1 h-1.5 rounded-full transition-colors ${
                s <= step ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground text-center">Step {step} of 3</p>

        {/* Step 1: Preset Selection */}
        {step === 1 && (
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Choose a preset policy stance</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Select a starting point that matches your business. You can adjust later.
              </p>
            </div>

            <RadioGroup value={preset} onValueChange={applyPreset} className="space-y-3">
              {Object.entries(PRESETS).map(([key, p]) => {
                const Icon = p.icon;
                return (
                  <Label
                    key={key}
                    htmlFor={key}
                    className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-all ${
                      preset === key
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/30'
                    }`}
                  >
                    <RadioGroupItem value={key} id={key} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-primary" />
                        <span className="font-medium">{p.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{p.description}</p>
                    </div>
                  </Label>
                );
              })}
            </RadioGroup>

            <div className="pt-2">
              <Label className="text-sm font-medium">Default fulfillment stance</Label>
              <Select value={defaultFulfillment} onValueChange={setDefaultFulfillment}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="split_shipment">Split shipment (ship what's ready now)</SelectItem>
                  <SelectItem value="ship_complete">Ship complete (wait until all items ready)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Step 2: Expedite Threshold */}
        {step === 2 && (
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Expedite budget thresholds</Label>
              <p className="text-xs text-muted-foreground mt-1">
                When should expedite requests require Slack approval?
              </p>
            </div>

            <div className="grid gap-4">
              <div>
                <Label htmlFor="maxExpedite" className="text-xs">Max expedite cost (USD)</Label>
                <Input
                  id="maxExpedite"
                  type="number"
                  value={maxExpedite}
                  onChange={(e) => setMaxExpedite(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Maximum expedite cost allowed</p>
              </div>

              <div>
                <Label htmlFor="expediteApproval" className="text-xs">Approval required over (USD)</Label>
                <Input
                  id="expediteApproval"
                  type="number"
                  value={expediteApprovalOver}
                  onChange={(e) => setExpediteApprovalOver(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Expedites above this amount route to Slack for approval
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-muted/50 border border-border p-3">
              <p className="text-xs text-muted-foreground">
                <strong>Tip:</strong> Keep "approval required" lower than max so bigger expedites route to Slack approvals while smaller ones auto-approve.
              </p>
            </div>
          </div>
        )}

        {/* Step 3: Refund Threshold */}
        {step === 3 && (
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Refund exception thresholds</Label>
              <p className="text-xs text-muted-foreground mt-1">
                When should refund exceptions require Slack approval?
              </p>
            </div>

            <div className="grid gap-4">
              <div>
                <Label htmlFor="maxRefund" className="text-xs">Max refund without approval (USD)</Label>
                <Input
                  id="maxRefund"
                  type="number"
                  value={maxRefundNoApproval}
                  onChange={(e) => setMaxRefundNoApproval(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Refunds up to this amount don't need approval
                </p>
              </div>

              <div>
                <Label htmlFor="refundApproval" className="text-xs">Approval required over (USD)</Label>
                <Input
                  id="refundApproval"
                  type="number"
                  value={refundApprovalOver}
                  onChange={(e) => setRefundApprovalOver(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Refunds above this amount route to Slack for approval
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-muted/50 border border-border p-3">
              <p className="text-xs text-muted-foreground">
                <strong>Note:</strong> Substitutions and channel buffers will be created with sensible defaults. You can edit them later in Settings → Policies.
              </p>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between gap-2 pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={saving || step === 1}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {step < 3 ? (
            <Button
              onClick={() => setStep((s) => Math.min(3, s + 1))}
              disabled={saving}
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleFinish}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Save policy profile
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
